import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AdminUser, userApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { isOwner } from '../../utils/roles';

const isCourseOwnerAccount = (item: AdminUser) => {
  const roles = [...(item.roles || []), item.role || ''].map((role) => role.toLowerCase());
  return roles.includes('courseowner') || roles.includes('coursecreator') || roles.includes('owner');
};

const ownerLabel = (item: Pick<AdminUser, 'username' | 'email'>) => {
  const name = item.username || item.email;
  return item.email && item.username && item.username !== item.email ? `${item.username} · ${item.email}` : name;
};

type ContentOwnerSelectProps = {
  value: number | '';
  currentLabel?: string;
  onChange: (authorId: number | '') => void;
};

const ContentOwnerSelect: React.FC<ContentOwnerSelectProps> = ({ value, currentLabel, onChange }) => {
  const { user } = useAuth();
  const canAssign = isOwner(user);
  const [owners, setOwners] = useState<AdminUser[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!canAssign) return;
    userApi.adminListUsers()
      .then((r) => setOwners((r.data.users || []).filter((item) => item.isActive && isCourseOwnerAccount(item))))
      .catch(() => setOwners([]));
  }, [canAssign]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const options = useMemo(() => {
    const list = [...owners];
    if (value && !list.some((item) => item.id === value)) {
      list.unshift({
        id: Number(value),
        email: currentLabel || `Owner #${value}`,
        username: currentLabel || `Owner #${value}`,
        mobile: '',
        isActive: true,
        isEmailVerified: true,
        role: 'CourseOwner',
      });
    }
    return list.sort((a, b) => (a.username || a.email).localeCompare(b.username || b.email));
  }, [owners, value, currentLabel]);

  const selected = options.find((item) => item.id === value);
  const display = selected ? ownerLabel(selected) : (currentLabel || '');
  const filtered = options.filter((item) => {
    const hay = `${item.username || ''} ${item.email || ''}`.toLowerCase();
    return hay.includes(query.trim().toLowerCase());
  });

  if (!canAssign) {
    return (
      <label className="block text-sm font-medium text-gray-700">
        Course owner
        <input
          className="mt-1 w-full rounded-md border-gray-300 bg-gray-50 text-gray-700"
          value={display || 'You (creator)'}
          readOnly
          disabled
        />
        <span className="mt-1 block text-xs text-gray-500">
          The creator is the course owner. Only a super admin (Owner) can reassign this.
        </span>
      </label>
    );
  }

  return (
    <div ref={rootRef} className="block text-sm font-medium text-gray-700">
      Course owner
      <div className="relative mt-1">
        <input
          className="w-full rounded-md border-gray-300"
          value={open ? query : display}
          placeholder="Search course owners"
          onFocus={() => {
            setQuery('');
            setOpen(true);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {open && (
          <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">No matching owners</li>
            )}
            {filtered.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-primary-50 ${item.id === value ? 'bg-primary-50 font-semibold text-primary-800' : 'text-gray-800'}`}
                  onClick={() => {
                    onChange(item.id);
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  {ownerLabel(item)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <span className="mt-1 block text-xs text-gray-500">
        Display name, LinkedIn URL, and other author details are taken from this owner’s profile.
      </span>
    </div>
  );
};

export default ContentOwnerSelect;
